/**
 * SeasonalAnalysis Component
 * Used in: Dashboard screen - displays Seasonal Impact Analysis
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FinancialData } from '../data/financialData';
import { Snowflake, Flower, Sun, Leaf, Thermometer } from 'lucide-react';

interface SeasonalAnalysisProps {
  data: FinancialData[];
}

export const SeasonalAnalysis: React.FC<SeasonalAnalysisProps> = ({ data }) => {
  // Calculate seasonal averages from the data
  const seasonalAverages = React.useMemo(() => {
    const seasonalGroups = data.reduce((acc, item) => {
      if (!acc[item.season]) {
        acc[item.season] = [];
      }
      acc[item.season].push(item);
      return acc;
    }, {} as Record<string, FinancialData[]>);

    return Object.entries(seasonalGroups).map(([season, seasonData]) => {
      const count = seasonData.length;
      return {
        season,
        avgIncome: seasonData.reduce((sum, item) => sum + item.totalIncome, 0) / count,
        avgCOGS: seasonData.reduce((sum, item) => sum + item.totalCOGS, 0) / count,
        avgGrossProfit: seasonData.reduce((sum, item) => sum + item.grossProfit, 0) / count,
      };
    }).sort((a, b) => {
      const seasonOrder = ['Winter', 'Spring', 'Summer', 'Fall'];
      return seasonOrder.indexOf(a.season) - seasonOrder.indexOf(b.season);
    });
  }, [data]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getSeasonIcon = (season: string) => {
    switch (season) {
      case 'Winter': return <Snowflake className="w-5 h-5 text-blue-500" />;
      case 'Spring': return <Flower className="w-5 h-5 text-green-500" />;
      case 'Summer': return <Sun className="w-5 h-5 text-yellow-500" />;
      case 'Fall': return <Leaf className="w-5 h-5 text-orange-500" />;
      default: return null;
    }
  };

  const getSeasonColor = (season: string) => {
    switch (season) {
      case 'Winter': return '#3b82f6';
      case 'Spring': return '#10b981';
      case 'Summer': return '#f59e0b';
      case 'Fall': return '#f97316';
      default: return '#6b7280';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            {getSeasonIcon(label)}
            <p className="font-semibold text-gray-900">{label}</p>
          </div>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Thermometer className="w-5 h-5 text-orange-600" />
        Seasonal Performance Analysis
      </h3>
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Average performance by season shows clear patterns related to weather and business cycles.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {seasonalAverages.map((season) => (
            <div key={season.season} className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-2">
                {getSeasonIcon(season.season)}
              </div>
              <div className="text-xs font-medium text-gray-700">{season.season}</div>
              <div className="text-sm font-bold text-gray-900">
                {formatCurrency(season.avgIncome)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <BarChart data={seasonalAverages} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="season" 
            stroke="#6b7280"
            fontSize={12}
            interval={0}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={formatCurrency}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          <Bar 
            dataKey="avgIncome" 
            name="Avg Income"
            fill="#3b82f6"
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="avgCOGS" 
            name="Avg COGS"
            fill="#ef4444"
            radius={[4, 4, 0, 0]}
          />
                  </BarChart>
          </ResponsiveContainer>
        </div>
    </div>
  );
};